import { initializeApp } from "firebase/app";
import { getAuth, GoogleAuthProvider, signInWithPopup, signOut, onAuthStateChanged, deleteUser, reauthenticateWithPopup, createUserWithEmailAndPassword, signInWithEmailAndPassword, sendPasswordResetEmail, sendEmailVerification, EmailAuthProvider, reauthenticateWithCredential, connectAuthEmulator, applyActionCode, verifyPasswordResetCode, confirmPasswordReset } from "firebase/auth";
import { getFirestore, doc, getDoc, setDoc, updateDoc, deleteDoc, addDoc, collection, query, where, getDocs, connectFirestoreEmulator, writeBatch, serverTimestamp } from "firebase/firestore";
import { getFunctions, httpsCallable, connectFunctionsEmulator } from "firebase/functions";
import { getStorage, ref as storageRef, uploadBytes, getDownloadURL, connectStorageEmulator } from "firebase/storage";
import { CLAUDE_SONNET_MODEL } from "./constants";

// ─── Offline dev mode ────────────────────────────────────────────────────────
// Set REACT_APP_OFFLINE_DEV=true in .env.local to run without Firebase/Google.
// .env.local is gitignored and never deployed — delete it to restore normal mode.
const OFFLINE = process.env.REACT_APP_OFFLINE_DEV === 'true';

if (OFFLINE) {
  console.warn('[offline-dev] Firebase bypassed. Data stored in localStorage.');
}

const DEV_USER = {
  uid: 'dev-uid-001',
  email: 'dev@speafy.local',
  displayName: 'Dev User',
  emailVerified: true,
  providerData: [],
  // Stub matching the real firebase.User shape — App.js's onAuthChange handler calls this
  // unconditionally (admin-role check) on every sign-in, real or dev. No custom claims for
  // the dev user by default (not an admin account).
  getIdTokenResult: () => Promise.resolve({ claims: {} }),
};

function devRead(key) {
  try { return JSON.parse(localStorage.getItem('speafy_dev_' + key)); } catch { return null; }
}

function devWrite(key, value) {
  localStorage.setItem('speafy_dev_' + key, JSON.stringify(value));
}

// Shallow-merge like Firestore's merge:true (top-level keys only)
function devMerge(key, incoming) {
  const existing = devRead(key) || {};
  devWrite(key, { ...existing, ...incoming });
}

// ─── Real Firebase (production) ──────────────────────────────────────────────
const firebaseConfig = {
  apiKey: "AIzaSyBtOzRdZYd0UgdDWbTfAdMEyPwi73lsuZA",
  authDomain: "language-teaching-project.firebaseapp.com",
  projectId: "language-teaching-project",
  storageBucket: "language-teaching-project.firebasestorage.app",
  messagingSenderId: "719410194534",
  appId: "1:719410194534:web:42e4e34837ea9e3980e5a7"
};

const app = initializeApp(firebaseConfig);

export const auth           = OFFLINE ? null : getAuth(app);
export const db             = OFFLINE ? null : getFirestore(app);
export const googleProvider = OFFLINE ? null : new GoogleAuthProvider();
const _functions            = OFFLINE ? null : getFunctions(app);
const _storage              = OFFLINE ? null : getStorage(app);

// ─── Local emulator dev mode ──────────────────────────────────────────────────
// Set REACT_APP_USE_EMULATORS=true in .env.local to point this app at the
// local Firebase emulators (firestore/auth/functions) instead of production.
// Never active in a real build/deploy — .env.local is gitignored and this
// flag defaults to off. Mutually exclusive with REACT_APP_OFFLINE_DEV.
if (!OFFLINE && process.env.REACT_APP_USE_EMULATORS === 'true') {
  connectAuthEmulator(auth, 'http://127.0.0.1:9099', { disableWarnings: true });
  connectFirestoreEmulator(db, '127.0.0.1', 8080);
  connectFunctionsEmulator(_functions, '127.0.0.1', 5001);
  connectStorageEmulator(_storage, '127.0.0.1', 9199);
  console.warn('[emulator-dev] Connected to local Firebase emulators.');
}

// ─── Auth exports ─────────────────────────────────────────────────────────────

export function signInWithGoogle() {
  if (OFFLINE) return Promise.resolve({ user: DEV_USER });
  return signInWithPopup(auth, googleProvider);
}

export function signUpWithEmail(email, password) {
  if (OFFLINE) return Promise.resolve({ user: DEV_USER });
  return createUserWithEmailAndPassword(auth, email, password);
}

export function signInWithEmail(email, password) {
  if (OFFLINE) return Promise.resolve({ user: DEV_USER });
  return signInWithEmailAndPassword(auth, email, password);
}

// Routes Firebase's action-link emails (verification / password reset) to our
// own branded /auth/action screen instead of Firebase's default unbranded
// hosted page. Without this, the links land on Firebase's generic continuation
// page ("Your email has been verified...") rather than AuthActionScreen.js.
function actionCodeSettings() {
  return { url: `${window.location.origin}/auth/action`, handleCodeInApp: false };
}

export function sendPasswordReset(email) {
  if (OFFLINE) return Promise.resolve();
  return sendPasswordResetEmail(auth, email, actionCodeSettings());
}

export function resendVerificationEmail() {
  if (OFFLINE) return Promise.resolve();
  return sendEmailVerification(auth.currentUser, actionCodeSettings());
}

export function signOutUser() {
  if (OFFLINE) return Promise.resolve();
  return signOut(auth);
}

// ─── Auth action-code handlers (email verification / password reset links) ────
// Used by /auth/action, the custom-domain landing page for Firebase Auth email
// action links (see AuthActionScreen.js). oobCode is the single-use code Firebase
// puts in those links' query string.

export function applyEmailVerificationCode(oobCode) {
  if (OFFLINE) return Promise.resolve();
  return applyActionCode(auth, oobCode);
}

export function verifyPasswordResetOobCode(oobCode) {
  if (OFFLINE) return Promise.resolve("dev@speafy.local");
  return verifyPasswordResetCode(auth, oobCode);
}

export function confirmNewPasswordWithCode(oobCode, newPassword) {
  if (OFFLINE) return Promise.resolve();
  return confirmPasswordReset(auth, oobCode, newPassword);
}

export function onAuthChange(callback) {
  if (OFFLINE) {
    // Auto sign-in as dev user on next tick
    setTimeout(() => callback(DEV_USER), 0);
    return () => {};
  }
  return onAuthStateChanged(auth, callback);
}

// ─── Firestore exports ────────────────────────────────────────────────────────

export async function loadUserData(uid) {
  if (OFFLINE) return devRead('user_' + uid);
  const snap = await getDoc(doc(db, "users", uid));
  return snap.exists() ? snap.data() : null;
}

export async function saveUserData(uid, data) {
  if (OFFLINE) { devMerge('user_' + uid, data); return; }
  // Strip all server-managed fields before writing — defense in depth alongside rules.
  // trialStartDate: onUserCreated Cloud Function only.
  // frozenTopicIds: computeFreeze Cloud Function only.
  // subscription:   Paddle webhook handler (Cloud Run proxy) only.
  // profile.role:       setUserRole Cloud Function (admin app) only.
  // profile.referredBy: linkStudentToTeacher Cloud Function / the referral pipeline only.
  const { trialStartDate: _tsd, frozenTopicIds: _fti, subscription: _sub, ...clean } = data;
  if (clean.profile) {
    const { trialStartDate: _ptsd, frozenTopicIds: _pfti, role: _prole, referredBy: _pref, ...cleanProfile } = clean.profile;
    clean.profile = cleanProfile;
  }
  // Same server-managed protection, one level down: computeFreeze also archives a per-language
  // snapshot at languages.{langCode}.frozenTopicIds (see functions/index.js) — without stripping
  // it here too, a client could forge languages.{code}.frozenTopicIds directly and later unlock
  // it just by switching to that language, bypassing the flat-field strip above entirely.
  if (clean.languages) {
    clean.languages = Object.fromEntries(
      Object.entries(clean.languages).map(([code, entry]) => {
        const { frozenTopicIds: _lfti, ...cleanEntry } = entry || {};
        return [code, cleanEntry];
      })
    );
  }
  await setDoc(doc(db, "users", uid), clean, { merge: true });
}

/** Asks the server to verify trial expiry and write frozenTopicIds if needed. */
export async function computeFreeze() {
  if (OFFLINE) return { frozen: false, frozenTopicIds: null };
  const fn = httpsCallable(_functions, "computeFreeze");
  const result = await fn();
  return result.data;
}

/**
 * Teacher-only. Returns { students: [{ firstName, joinedAt, subscriptionStatus }], count, capped }.
 * Server-side (getMyReferredStudents Cloud Function) so a teacher's client never reads another
 * user's document — same cross-user-read-avoidance as the denormalized name on profile.referredBy.
 * The callable itself re-checks that the caller holds the teacher role.
 */
export async function getMyReferredStudents() {
  if (OFFLINE) return { students: [], count: 0, capped: false };
  const fn = httpsCallable(_functions, "getMyReferredStudents");
  const result = await fn();
  return result.data;
}

/**
 * Teacher-only. Returns { balanceCents, thresholdCents, eligible, hasOpenRequest } — the caller's
 * OWN unpaid commission balance, computed fresh server-side every call (never cached). Powers
 * the Teacher Dashboard's "Request Payout" section: whether to show/enable the button
 * (eligible) and whether to show an "already requested" state instead (hasOpenRequest).
 */
export async function getMyPayoutStatus() {
  if (OFFLINE) return { balanceCents: 0, thresholdCents: 5000, eligible: false, hasOpenRequest: false };
  const fn = httpsCallable(_functions, "getMyPayoutStatus");
  const result = await fn();
  return result.data;
}

/**
 * Teacher-only. Submits a payout request — creates a payouts/{id} doc with status: "requested"
 * and emails the admin. Does NOT move any money; it only starts the manual payout process.
 * The callable re-checks eligibility and re-checks for an existing open request server-side —
 * never trust the client's own idea of either. Returns { success, payoutId, requestedBalanceCents }.
 * Throws (failed-precondition) if the caller isn't eligible or already has an open request.
 */
export async function requestPayout() {
  if (OFFLINE) return { success: true, payoutId: "offline", requestedBalanceCents: 0 };
  const fn = httpsCallable(_functions, "requestPayout");
  const result = await fn();
  return result.data;
}

/**
 * Reads THIS user's acceptance record for a given Teacher Partner Terms version, or null if they
 * haven't accepted that version. Doc id is the version string. The Home banner + the dashboard
 * "Accepted {date}" line both key off this — never off a client-writable profile flag.
 * Returns { termsVersion, acceptedAt: <ISO string|null>, ... } | null.
 */
export async function loadTeacherTermsAcceptance(uid, version) {
  if (OFFLINE || !uid || !version || version === "DRAFT-PENDING-DATE") return null;
  try {
    const snap = await getDoc(doc(db, "users", uid, "teacherTermsAcceptances", version));
    if (!snap.exists()) return null;
    const d = snap.data();
    const ts = d.acceptedAt;
    const acceptedAt = ts?.toDate ? ts.toDate().toISOString()
      : ts?.seconds ? new Date(ts.seconds * 1000).toISOString()
      : null;
    return { ...d, acceptedAt };
  } catch (e) {
    // Rules not deployed / transient — treat as "unknown", caller falls back to showing the banner.
    console.warn("loadTeacherTermsAcceptance failed:", e);
    return null;
  }
}

/**
 * Records the caller's acceptance of the current Teacher Partner Terms (acceptTeacherPartnerTerms
 * Cloud Function — the only path that writes the audit record + emails support). Pass the version
 * and hash the client just rendered so the server can reject a stale client.
 * Returns { accepted?: true, alreadyAccepted?: true, termsVersion, acceptedAt }.
 */
export async function acceptTeacherPartnerTerms({ termsVersion, termsHash }) {
  if (OFFLINE) return { accepted: true, termsVersion, acceptedAt: new Date().toISOString() };
  const fn = httpsCallable(_functions, "acceptTeacherPartnerTerms");
  const result = await fn({ termsVersion, termsHash });
  return result.data;
}

// ─── Firebase Storage ─────────────────────────────────────────────────────────
// The Teacher Dashboard's profile-photo upload is the app's only Storage use so far.
export const TEACHER_PHOTO_MAX_BYTES = 200 * 1024;      // 200 KB
export const TEACHER_PHOTO_TYPES = ["image/png", "image/jpeg"];

/**
 * Uploads a teacher's profile photo to Storage at teacherPhotos/{uid} (overwrites any
 * previous one) and returns its public download URL. The caller pre-validates type/size for a
 * good error message, but storage.rules enforces both again server-side — PNG/JPG, under 200 KB,
 * owner-write-only — so a bypassed client check still can't write a bad object.
 */
export async function uploadTeacherPhoto(uid, file) {
  if (!uid || !file) throw new Error("uploadTeacherPhoto: uid and file are required");
  if (OFFLINE) return URL.createObjectURL(file);   // dev-only stand-in, no real upload
  const r = storageRef(_storage, `teacherPhotos/${uid}`);
  await uploadBytes(r, file, { contentType: file.type });
  return getDownloadURL(r);
}

// ─── Translation "cache" collections (translations/, grammarLabTranslations/,
// placementTranslations/) ────────────────────────────────────────────────────
// Despite the name, these are NOT disposable/ephemeral caches — they are the permanent home
// for AI-generated translations, some of which get manually reviewed and marked `verified: true`
// (see setGrammarLabTranslationCache below). "Cache" describes the ACCESS PATTERN (check
// Firestore first, only call Claude on a miss, then persist the result) — it does not mean this
// data is safe to bulk-delete/regenerate. Doing so would silently discard human-verified
// translations and replace them with fresh, unverified machine output. Staleness is handled
// narrowly instead, via the per-doc `contentHash` check below (source Turkish text changed =
// treat as a miss and regenerate just that doc) — never a blanket wipe.
//
// Cheap, deterministic (non-cryptographic) hash of the exact Turkish content a cached
// translation is derived from — turkish/example_1/example_2/example_3 per word, in array order.
// Stored as a `contentHash` FIELD on the cache doc (see get/setTranslationCache below), checked
// at read time — NOT folded into the doc id itself. An earlier version of this fix put the hash
// in the id (`${topicId}_${lang}_${hash}`), but several ops scripts (build-search-index.js,
// prewarm-translations.js, refresh-*-cache.js) enumerate/look up these docs by guessing
// `${topicId}_${lang}` directly; changing the id format would've made every one of those silently
// find nothing, breaking search indexing. Keeping the id stable and validating freshness via a
// field means those scripts keep working unmodified (they just don't get staleness-awareness for
// free — search-index freshness is a separate, lower-stakes gap, not addressed here).
//
// This is the root fix for the "ceviz" mistranslation bug (2026-08): T0100's Farsi cache kept
// serving a translation of "Ceviz istiyorum." for weeks after the sentence was corrected to
// "Ceviz küçük ve kahverengi.", because nothing about that content edit ever invalidated the
// cache. Now a mismatched contentHash is treated as a plain cache miss (regenerate + overwrite)
// instead of a silent stale hit — no manual invalidation script required for this bug class.
function hashWords(words) {
  const str = JSON.stringify((words || []).map(w => [w.turkish, w.example_1 ?? w.example, w.example_2, w.example_3]));
  let h = 5381;
  for (let i = 0; i < str.length; i++) h = ((h * 33) ^ str.charCodeAt(i)) >>> 0;
  return h.toString(36);
}

/** `words` must be the exact array about to be (or previously) sent to translateLessonWords for
 * this topic (post curriculum-corrections merge, where the caller applies one) — this is what
 * getTranslationCache checks the cached contentHash against, and what setTranslationCache stamps
 * onto the doc it writes. */
// langCode (e.g. "turkish") is part of the cache key so a second target language's topic_ids —
// almost certainly reusing this app's own T#### numbering convention — can never collide with
// Turkish's cached translations. Existing pre-fix docs (keyed without a langCode prefix) are
// simply orphaned, not migrated: they self-heal via the same cache-miss regeneration path a
// contentHash mismatch already uses below, at a one-time regeneration cost. Approved tradeoff —
// see the "let them regenerate" decision over migrating doc IDs in place.
export async function getTranslationCache(langCode, topicId, lang, words) {
  const cacheKey = `${langCode}_${topicId}_${lang}`;
  const data = OFFLINE ? devRead('trans_' + cacheKey) : await getDoc(doc(db, "translations", cacheKey)).then(s => s.exists() ? s.data() : null);
  if (!data) return null;
  // No contentHash on the doc = written before this fix shipped; treat as unknown/stale rather
  // than trusting it blindly, same as a genuine mismatch — regenerates once, then self-heals.
  if (data.contentHash !== hashWords(words)) return null;
  return data.words;
}

export async function setTranslationCache(langCode, topicId, lang, words, data) {
  const cacheKey = `${langCode}_${topicId}_${lang}`;
  // translateLessonWords (the only producer of `data.words` here) only ever returns
  // meaning/exTr (example_1) — it never generates ex2Tr/ex3Tr. A plain overwrite would
  // therefore silently delete any example_2/3 translations previously backfilled by
  // scripts/prewarm-translations.js --fields=examples23 every time example_1 alone
  // regenerates (e.g. a contentHash miss from an unrelated edit). Merge forward instead: keep
  // each word's existing ex2Tr/ex3Tr unless this write's words explicitly provide their own.
  // Only add the key when there's an actual string to preserve — `{ ex2Tr: existingWords[i]?.ex2Tr,
  // ...w }` used to set literal `ex2Tr: undefined` whenever there was no prior doc (e.g. the very
  // first time a topic+language is ever cached), and Firestore's client SDK hard-rejects any
  // field with an `undefined` value (throws invalid-argument before the write is even sent) —
  // silently breaking every first-time cache write for a topic with no earlier translations.
  const existing = OFFLINE ? devRead('trans_' + cacheKey) : await getDoc(doc(db, "translations", cacheKey)).then(s => s.exists() ? s.data() : null);
  const existingWords = existing?.words || [];
  const mergedWords = data.words.map((w, i) => {
    const preserved = {};
    if (typeof existingWords[i]?.ex2Tr === "string") preserved.ex2Tr = existingWords[i].ex2Tr;
    if (typeof existingWords[i]?.ex3Tr === "string") preserved.ex3Tr = existingWords[i].ex3Tr;
    return { ...preserved, ...w };
  });
  const payload = { ...data, words: mergedWords, contentHash: hashWords(words), verified: false, generatedAt: new Date().toISOString(), model: CLAUDE_SONNET_MODEL };
  if (OFFLINE) { devWrite('trans_' + cacheKey, payload); return; }
  await setDoc(doc(db, "translations", cacheKey), payload);
}

// Merge-write of just ex2Tr/ex3Tr onto an existing Layer 2 translation cache doc — the lazy-fill
// sibling to setTranslationCache's own ex2Tr/ex3Tr preservation. `mergedWords` must be the FULL
// words array for this doc (every existing entry carried through untouched, only the
// previously-missing indices replaced) — same "read full array, replace, write full array" shape
// scripts/prewarm-translations.js's generateExamples23 uses, since a Firestore merge-write
// replaces an array field wholesale rather than merging inside it. Each newly-filled word is
// tagged ex23Source:"lazy" (the batch backfill script never sets this field) purely so a future
// admin pass can find and spot-check lazily-generated translations without re-scanning everything.
export async function setExamples23Cache(langCode, topicId, lang, mergedWords) {
  const cacheKey = `${langCode}_${topicId}_${lang}`;
  if (OFFLINE) { devWrite('trans_' + cacheKey, { ...(devRead('trans_' + cacheKey) || {}), words: mergedWords }); return; }
  await setDoc(doc(db, "translations", cacheKey), { words: mergedWords }, { merge: true });
}

// Live curriculum correction overrides for one topic — a patch layer over the static
// src/curriculum.js content, written only by the admin app's applyContentCorrection callable.
// One query per topic load (not per word) — LessonView merges the result onto topicData.words
// by word_id. Returns [] when nothing has ever been corrected for this topic, which is the
// common case.
// Filters by langCode in memory rather than in the query itself: a correction written before
// this per-language fix shipped has no langCode field at all, and a strict `where("langCode",
// "==", langCode)` equality filter would silently exclude it (Firestore equality filters never
// match a missing field) — those pre-fix docs are unambiguously Turkish's own corrections
// (nothing else existed when they were written), so a missing field defaults to "turkish" here,
// the same "legacy = turkish" reasoning used for grammarProgress/frozenTopicIds elsewhere.
export async function getCurriculumCorrections(topicId, langCode) {
  if (OFFLINE) return [];
  const snap = await getDocs(query(collection(db, "curriculum_corrections"), where("topic_id", "==", topicId)));
  return snap.docs.map(d => d.data()).filter(c => (c.langCode || "turkish") === langCode);
}

// Grammar Lab's own translation cache — same cache-first shape as
// getTranslationCache/setTranslationCache above (Layer 2 vocab), but a separate collection
// since the doc shape differs (title/explanation/ruleSummary/forms_or_items, not words[]) and
// carries a verification_status tier flag that Layer 2 translations don't have.
export async function getGrammarLabTranslationCache(cacheKey) {
  if (OFFLINE) return devRead('grammarLabTrans_' + cacheKey);
  const snap = await getDoc(doc(db, "grammarLabTranslations", cacheKey));
  return snap.exists() ? snap.data() : null;
}

export async function setGrammarLabTranslationCache(cacheKey, data) {
  const payload = {
    ...data,
    verification_status: data.verification_status || "unverified_machine_translation",
    translation_pipeline: "single_pass_sonnet5",
    generatedAt: new Date().toISOString(),
    model: CLAUDE_SONNET_MODEL,
  };
  if (OFFLINE) { devWrite('grammarLabTrans_' + cacheKey, payload); return; }
  await setDoc(doc(db, "grammarLabTranslations", cacheKey), payload);
}

// Placement test's per-item translation cache — one doc per (word or grammar-form) meaning,
// keyed by `${itemId}_${baseLanguage}`, since a round only ever needs a handful of individual
// answer strings translated (unlike Lesson/Grammar Lab's per-topic/per-point doc shape above).
export async function getPlacementTranslationCache(cacheKey) {
  if (OFFLINE) return devRead('placementTrans_' + cacheKey);
  const snap = await getDoc(doc(db, "placementTranslations", cacheKey));
  return snap.exists() ? snap.data().text : null;
}

export async function setPlacementTranslationCache(cacheKey, text) {
  const payload = { text, generatedAt: new Date().toISOString(), model: "claude-haiku-4-5-20251001" };
  if (OFFLINE) { devWrite('placementTrans_' + cacheKey, payload); return; }
  await setDoc(doc(db, "placementTranslations", cacheKey), payload);
}

export async function getUIStrings(lang) {
  if (OFFLINE) return null;
  const snap = await getDoc(doc(db, "ui_strings", lang));
  return snap.exists() ? snap.data() : null;
}

export async function setUIStrings(lang, data) {
  if (OFFLINE) return;
  await setDoc(doc(db, "ui_strings", lang), data);
}

export async function updateLanguageData(uid, langCode, data) {
  if (OFFLINE) {
    const existing = devRead('user_' + uid) || {};
    const langs = existing.languages || {};
    devWrite('user_' + uid, { ...existing, languages: { ...langs, [langCode]: data } });
    return;
  }
  try {
    await updateDoc(doc(db, "users", uid), { [`languages.${langCode}`]: data });
  } catch (e) {
    if (e.code === "not-found") {
      await setDoc(doc(db, "users", uid), { languages: { [langCode]: data } }, { merge: true });
    } else {
      throw e;
    }
  }
}

export async function saveReport(data) {
  if (OFFLINE) { console.log('[offline-dev] report (not saved):', data); return; }
  await addDoc(collection(db, "reports"), data);
}

// Raw per-review event log (cardId/deck/rating/timestamp), one doc per flashcard grade.
// Not read by anything in the app today — it exists purely so real review history
// accumulates in the background, in case FSRS parameters are ever retrained on actual
// usage instead of the generic community defaults. Best-effort: callers should not let a
// logging failure block the real card-state save (see handleCardReview in App.js).
export async function logReviewEvents(uid, events) {
  if (!events.length) return;
  if (OFFLINE) { console.log('[offline-dev] review log (not saved):', events); return; }
  const batch = writeBatch(db);
  for (const e of events) {
    batch.set(doc(collection(db, "reviewLogs")), { uid, reviewedAt: serverTimestamp(), ...e });
  }
  await batch.commit();
}

// Best-effort client-side crash capture — the app has no error-reporting service, so an
// uncaught render error (ErrorBoundary) or global JS error (window.onerror/unhandledrejection
// in index.js) would otherwise leave zero trace once the tab reloads. Never throws itself: a
// failed log write must not compound whatever crash it's trying to record. Field values are
// coerced to strings client-side to satisfy the `clientErrors` create rule (which requires
// every field to be a string), since e.g. `error.stack` can be undefined.
export async function logClientError(data) {
  if (OFFLINE) { console.error('[offline-dev] client error (not saved):', data); return; }
  try {
    await addDoc(collection(db, "clientErrors"), {
      message: String(data.message ?? "").slice(0, 2000),
      stack: String(data.stack ?? "").slice(0, 4000),
      componentStack: String(data.componentStack ?? "").slice(0, 4000),
      source: String(data.source ?? "unknown").slice(0, 50),
      uid: auth?.currentUser?.uid || null,
      userAgent: navigator.userAgent,
      url: window.location.href,
      timestamp: serverTimestamp(),
    });
  } catch { /* logging must never throw */ }
}

// Best-effort, per-session cost/usage instrumentation for the Babi chat widget (2026-09
// instrumentation task — see AgentChat.js's sessionMetricsRef). Written once, on unmount, from
// the counters accumulated across that mount's conversation. Exists purely to size two pending
// decisions with real data: how large a conversation-history trim window to use, and whether
// FEATURE_SUMMARY/LANDING_REFERENCE are worth moving behind an on-demand tool. Deliberately
// carries NO message content — only counts, tool names, and timing — same privacy posture as
// logReviewEvents/logClientError above. Never read by the app itself; a future analysis pass
// queries this collection directly (or via the admin app). Skipped entirely for a session that
// never sent a message (see the caller's turnCount > 0 guard) so this never fills with pure noise
// from someone who just opened and closed the panel.
export async function logBabiSessionMetrics(data) {
  if (OFFLINE) { console.log('[offline-dev] babi session metrics (not saved):', data); return; }
  try {
    await addDoc(collection(db, "babiSessionMetrics"), {
      uid: auth?.currentUser?.uid || null,
      startedAt: data.startedAt,
      durationMs: data.durationMs,
      turnCount: data.turnCount,
      apiCallCount: data.apiCallCount,
      toolRoundsPerTurn: data.toolRoundsPerTurn,
      toolCallCounts: data.toolCallCounts,
      pricingKeywordReplyCount: data.pricingKeywordReplyCount,
      apiCalls: data.apiCalls,
      loggedAt: serverTimestamp(),
    });
  } catch { /* logging must never throw */ }
}

export async function deleteAccount(emailPassword = null) {
  if (OFFLINE) return;
  const user = auth.currentUser;
  if (!user) throw new Error("Not signed in");
  const isEmailUser = user.providerData.some(p => p.providerId === "password");
  if (isEmailUser && emailPassword) {
    const credential = EmailAuthProvider.credential(user.email, emailPassword);
    await reauthenticateWithCredential(user, credential);
  }
  await deleteDoc(doc(db, "users", user.uid));
  try {
    await deleteUser(user);
  } catch (err) {
    if (err.code === "auth/requires-recent-login") {
      if (isEmailUser) {
        const e2 = new Error("Password required to delete account");
        e2.code = "auth/email-reauth-required";
        throw e2;
      }
      await reauthenticateWithPopup(user, googleProvider);
      await deleteUser(user);
    } else {
      throw err;
    }
  }
}
